import { MessageDesignTokens } from '@primeuix/themes/types/message';

import { createMessageTokens } from '../tokens/components.js';
import { SEMANTIC, type SemanticTokens } from '../tokens/semantic.js';

/**
 * Message Component Configuration
 */
export function createMessage(s: SemanticTokens): MessageDesignTokens {
  const messageTokens = createMessageTokens(s);
  return {
    root: { borderRadius: messageTokens.base.radius, borderWidth: '1px' },
    content: { padding: messageTokens.base.paddingX, gap: messageTokens.base.gap },
    text: {
      fontSize: messageTokens.base.bodyFontSize,
      fontWeight: messageTokens.base.bodyFontWeight,
    },
    simple: { content: { padding: '0' } },
    icon: { size: messageTokens.base.iconSize },
    closeButton: {
      width: messageTokens.base.closeButtonSize,
      height: messageTokens.base.closeButtonSize,
    },
    colorScheme: {
      light: {
        success: {
          background: messageTokens.success.bg,
          borderColor: messageTokens.success.border,
          color: messageTokens.success.fg,
          shadow: 'none',
        },
        info: {
          background: messageTokens.info.bg,
          borderColor: messageTokens.info.border,
          color: messageTokens.info.fg,
          shadow: 'none',
        },
        warn: {
          background: messageTokens.warning.bg,
          borderColor: messageTokens.warning.border,
          color: messageTokens.warning.fg,
          shadow: 'none',
        },
        error: {
          background: messageTokens.danger.bg,
          borderColor: messageTokens.danger.border,
          color: messageTokens.danger.fg,
          shadow: 'none',
        },
        secondary: {
          background: messageTokens.secondary.bg,
          borderColor: messageTokens.secondary.border,
          color: messageTokens.secondary.fg,
          shadow: 'none',
        },
      },
    },
    css: () => `
        /*
         * Center controls on the first text line, not the whole multiline message or a glyph baseline.
         * The text keeps its inherited line-height (16px body × 1.5 = lineHeight.xl); only the controls move.
         * Known limit: 18px/28px titles place controls 2px above the first-line center, within the 2px tolerance.
         * The review-wizard variant projects its own icon; these control rules do not apply to that icon.
         */
        :root .p-message .p-message-content .p-message-icon {
          align-self: flex-start;
          margin-top: calc((${s.typography.lineHeight.xl} - var(--p-message-icon-size)) / 2);
        }

        :root .p-message .p-message-content .p-message-close-button {
          align-self: flex-start;
          margin-top: calc(
            (${s.typography.lineHeight.xl} - var(--p-message-close-button-height, ${messageTokens.base.closeButtonSize})) / 2
          );
        }

        /* Review variant: card-like message used in rental wizard review step */
        p-message[pMessageReview],
        p-message[pmessagereview] {
          --p-message-border-width: 0;
          --p-message-success-border-color: transparent;
          --p-message-warn-border-color: transparent;
          --p-message-error-border-color: transparent;
          --p-message-secondary-border-color: transparent;
          --p-message-info-border-color: transparent;
          --p-message-success-shadow: ${messageTokens.base.shadow};
          --p-message-warn-shadow: ${messageTokens.base.shadow};
          --p-message-error-shadow: ${messageTokens.base.shadow};
          --p-message-secondary-shadow: ${messageTokens.base.shadow};
          --p-message-info-shadow: ${messageTokens.base.shadow};
        }

        p-message[pMessageReview] > .p-message,
        p-message[pmessagereview] > .p-message {
          border: none !important;
          border-width: 0 !important;
          border-color: transparent !important;
          outline: none !important;
          border-inline-width: 0 !important;
          border-block-width: 0 !important;
          border-inline-start-width: 0 !important;
          border-inline-end-width: 0 !important;
          box-shadow: ${messageTokens.base.shadow};
        }

        p-message[pMessageReview] .p-message-content,
        p-message[pmessagereview] .p-message-content {
          align-items: flex-start;
          gap: 10px;
          padding: ${messageTokens.base.paddingY};
          border: none !important;
          outline: none !important;
          border-inline-width: 0 !important;
          border-block-width: 0 !important;
        }

        p-message[pMessageReview] > .p-message.p-message-success,
        p-message[pmessagereview] > .p-message.p-message-success {
          background: ${messageTokens.success.bg};
          color: ${messageTokens.success.fg};
          border: none !important;
          border-color: transparent !important;
        }

        p-message[pMessageReview] > .p-message.p-message-warn,
        p-message[pmessagereview] > .p-message.p-message-warn {
          background: ${messageTokens.warning.bg};
          color: ${messageTokens.warning.fg};
          border: none !important;
          border-color: transparent !important;
        }

        p-message[pMessageReview] > .p-message.p-message-error,
        p-message[pmessagereview] > .p-message.p-message-error {
          background: ${messageTokens.danger.bg};
          color: ${messageTokens.danger.fg};
          border: none !important;
          border-color: transparent !important;
        }
      `,
  };
}
export const message = createMessage(SEMANTIC);
